// Project dashboard (V38-V40, X17-X18).
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { useProject } from "@/hooks/project";
import { Badge, Button, Card, Empty, Field, PageHeader, Select, Tabs, cn, fmtDate, fmtIdx, fmtMoney, fmtNum, fmtPct } from "@/components/ui";
import { BarChart, COLORS, Donut, LineChart } from "@/components/charts";
import { ExportButton } from "@/components/Importer";
import { useNeutral } from "@/lib/theme";
import { deliverableStatus, group, metricsAt } from "@/engine/compute";

export function Overview() {
  const { project, prep, periods, currentIdx, b, nodes, issueCount } = useProject();
  const [dbs, setDbs] = useState("");
  const [grain, setGrain] = useState<"period" | "month">("period");
  const N = useNeutral();
  const s = project.settings;
  const filter = dbs ? { dbs: [dbs] } : undefined;
  const { total, groups } = useMemo(() => group(prep, { by: ["dbs"], filter }), [prep, dbs]);
  if (!periods.length || !b.deliverables.length)
    return (
      <div className="max-w-3xl">
        <PageHeader title="Dashboard" sub={`${project.code} · ${project.name}`} />
        <Empty action={<Link to="/p/$projectId/setup" params={{ projectId: project._id }} className="text-brand-600 underline">Start with the setup</Link>}>
          The dashboard fills in once the project has a calendar and deliverables. Use the checklist on the left.
        </Empty>
      </div>
    );
  const i = Math.max(0, currentIdx);
  const m = metricsAt(total, i, s);
  const rate = s.laborRate;
  const idx = periods.map((_, k) => k).filter((k) => grain === "period" || periods[k].monthEnd);
  const series = idx.map((k) => metricsAt(total, k, s));
  const past = (k: number) => idx[k] <= i;
  const status = deliverableStatus(prep, i);
  const mix = { notStarted: 0, inProgress: 0, complete: 0, late: 0 };
  const cutoff = periods[i].cutoff;
  for (const st of status) {
    const d = b.deliverables.find((x) => x._id === st.id);
    if (dbs && d?.codes.dbs !== dbs) continue;
    if ((st.pctEarned ?? 0) >= 1 - 1e-9) mix.complete++;
    else {
      const next = d?.steps.find((x) => !x.earned);
      const due = next?.forecast ?? next?.planned;
      if (due && due < periods[i].start) mix.late++;
      else if ((st.pctEarned ?? 0) > 0) mix.inProgress++;
      else mix.notStarted++;
    }
  }
  const byDbs = groups
    .map((g) => ({ g, m: metricsAt(g, i, s), title: nodes.dbs.find((n) => n.code === g.key[0])?.title ?? "" }))
    .filter(({ m: x }) => x.bac || x.pv || x.ev || x.ac);
  const tile = (label: string, v: string, sub?: string, tone?: "good" | "bad") => (
    <div className="rounded-lg border border-slate-200 bg-surface p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={cn("num mt-0.5 text-xl font-semibold", tone === "good" && "text-emerald-700", tone === "bad" && "text-red-600")}>{v}</div>
      {sub && <div className="num text-xs text-slate-500">{sub}</div>}
    </div>
  );
  const idxTone = (v: number | null) => (v === null ? undefined : v >= 1 ? "good" : "bad");
  return (
    <div className="space-y-4">
      <PageHeader
        title="Dashboard"
        sub={
          <>
            {project.code} · {project.name} · as of P{periods[i].number} ({fmtDate(cutoff)})
            {issueCount.errors > 0 && <Badge tone="red" className="ml-2">{issueCount.errors} data errors</Badge>}
          </>
        }
        actions={
          <>
            <Field label="Discipline">
              <Select value={dbs} onChange={(e) => setDbs(e.target.value)} className="w-48" aria-label="Dashboard discipline">
                <option value="">All</option>
                {nodes.dbs.map((n) => (
                  <option key={n.code} value={n.code}>
                    {n.code} · {n.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Tabs value={grain} onChange={setGrain} items={[{ value: "period", label: "Periods" }, { value: "month", label: "Months" }]} />
            <ExportButton
              file={`${project.code}-dashboard.xlsx`}
              sheets={() => [
                {
                  name: "Dashboard",
                  headerRow: 3,
                  rows: [
                    [`${project.code} ${project.name}`, `As of ${cutoff}`],
                    ["Budget", m.bac, "Planned %", m.pctPlanned, "Earned %", m.pctEarned, "SPI", m.spi, "CPI", m.cpi, "CSI", m.csi, "EAC", m.eac1, "VAC", m.vac],
                    [],
                    ["DBS", "Title", "Budget", "Planned %", "Earned %", "Actuals %", "SPI", "CPI", "EAC", "VAC"],
                    ...byDbs.map(({ g, m: x, title }) => [g.key[0] || "(none)", title, x.bac, x.pctPlanned, x.pctEarned, x.pctExpended, x.spi, x.cpi, x.eac1, x.vac]),
                  ],
                },
              ]}
            />
            <Button onClick={() => window.print()}>
              <Printer className="size-4" /> PDF
            </Button>
          </>
        }
      />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        {tile("Budget (h)", fmtNum(m.bac), rate ? fmtMoney(m.bac * rate) : undefined)}
        {tile("Planned", fmtPct(m.pctPlanned), `${fmtNum(m.pv)} h`)}
        {tile("Earned", fmtPct(m.pctEarned), `${fmtNum(m.ev)} h`)}
        {tile("Actuals", fmtPct(m.pctExpended), `${fmtNum(m.ac)} h`)}
        {tile("SPI", fmtIdx(m.spi), `SV ${fmtNum(m.sv)} h`, idxTone(m.spi))}
        {tile("CPI", fmtIdx(m.cpi), `CV ${fmtNum(m.cv)} h`, idxTone(m.cpi))}
        {tile("CSI", fmtIdx(m.csi), undefined, idxTone(m.csi))}
        {tile("EAC (h)", fmtNum(m.eac1), `VAC ${fmtNum(m.vac)}${rate && m.vac !== null ? ` · ${fmtMoney(m.vac * rate)}` : ""}`, m.vac === null ? undefined : m.vac >= 0 ? "good" : "bad")}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <LineChart
            title="S-curve"
            name={`${project.code}-progress`}
            labels={idx.map((k) => fmtDate(periods[k].cutoff))}
            marker={idx.findIndex((k) => k >= i)}
            height={300}
            yFormat={(v) => `${Math.round(v * 100)}%`}
            series={[
              { key: "p", label: "Planned", color: COLORS.planned, values: series.map((x) => x.pctPlanned) },
              { key: "f", label: "Forecast", color: COLORS.forecast, values: series.map((x) => x.pctForecast), dashed: true },
              { key: "e", label: "Earned", color: COLORS.earned, values: series.map((x, k) => (past(k) ? x.pctEarned : null)) },
              { key: "a", label: "Actuals", color: COLORS.actual, values: series.map((x, k) => (past(k) ? x.pctExpended : null)) },
            ]}
          />
        </Card>
        <Card>
          <Donut
            title="Deliverable status"
            data={[
              { label: "Complete", value: mix.complete, color: COLORS.earned },
              { label: "In progress", value: mix.inProgress, color: COLORS.planned },
              { label: "Not started", value: mix.notStarted, color: N.axis },
              { label: "Late", hint: "Next milestone date has passed", value: mix.late, color: COLORS.actual },
            ]}
          />
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <BarChart
            title="Planned vs earned by discipline"
            name={`${project.code}-by-discipline`}
            horizontal
            labels={byDbs.map((x) => x.g.key[0] || "(none)")}
            height={Math.max(220, byDbs.length * 34 + 70)}
            yFormat={(v) => `${Math.round(v * 100)}%`}
            series={[
              { key: "p", label: "Planned %", color: COLORS.planned, values: byDbs.map((x) => x.m.pctPlanned) },
              { key: "e", label: "Earned %", color: COLORS.earned, values: byDbs.map((x) => x.m.pctEarned) },
            ]}
          />
        </Card>
        <Card>
          <LineChart
            title="Performance indices (cumulative)"
            name={`${project.code}-indices`}
            labels={idx.map((k) => fmtDate(periods[k].cutoff))}
            height={260}
            marker={idx.findIndex((k) => k >= i)}
            yFormat={(v) => v.toFixed(2)}
            series={[
              { key: "spi", label: "SPI", color: COLORS.planned, values: series.map((x, k) => (past(k) ? x.spi : null)) },
              { key: "cpi", label: "CPI", color: COLORS.actual, values: series.map((x, k) => (past(k) ? x.cpi : null)) },
            ]}
          />
        </Card>
      </div>
      <Card title="By discipline" bodyClass="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-right text-xs text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">DBS</th>
              <th className="px-3 py-2">Budget</th>
              <th className="px-3 py-2">Planned</th>
              <th className="px-3 py-2">Earned</th>
              <th className="px-3 py-2">Actuals</th>
              <th className="px-3 py-2">SPI</th>
              <th className="px-3 py-2">CPI</th>
              <th className="px-3 py-2">CSI</th>
              <th className="px-3 py-2">EAC</th>
              <th className="px-3 py-2">VAC</th>
            </tr>
          </thead>
          <tbody>
            {byDbs.map(({ g, m: x, title }) => (
              <tr key={g.key[0]} className="num border-t border-slate-100 text-right">
                <td className="px-3 py-1.5 text-left">
                  <b>{g.key[0] || "(none)"}</b> <span className="text-slate-500">{title}</span>
                </td>
                <td className="px-3 py-1.5">{fmtNum(x.bac)}</td>
                <td className="px-3 py-1.5">{fmtPct(x.pctPlanned)}</td>
                <td className="px-3 py-1.5 font-medium">{fmtPct(x.pctEarned)}</td>
                <td className="px-3 py-1.5">{fmtPct(x.pctExpended)}</td>
                <td className={cn("px-3 py-1.5", (x.spi ?? 1) < 1 && "text-red-600")}>{fmtIdx(x.spi)}</td>
                <td className={cn("px-3 py-1.5", (x.cpi ?? 1) < 1 && "text-red-600")}>{fmtIdx(x.cpi)}</td>
                <td className="px-3 py-1.5">{fmtIdx(x.csi)}</td>
                <td className="px-3 py-1.5">{fmtNum(x.eac1)}</td>
                <td className={cn("px-3 py-1.5", (x.vac ?? 0) < 0 && "text-red-600")}>{fmtNum(x.vac)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
